export type Translations = Record<string, Record<string, unknown>>;

export const translations: Translations = {
  en: {
    title: 'USA Tax Calculator 2025',
    subtitle:
      'Estimate your 2025 federal and state income taxes with updated rates and deductions.',
    disclaimer: 'For planning only. Not tax or legal advice. Consult a professional for filing.',
    privacyNotice:
      'Privacy Notice: All calculations run locally in your browser. No data is sent to a server.',
    disclaimerBanner: {
      title: 'Tax Estimation Tool - Not Professional Advice',
      summary:
        'This calculator provides estimates only. Results may differ from actual tax liability. For accurate tax filing, consult a qualified tax professional or use IRS-approved software.',
      accuracy: 'Accuracy:',
      accuracyText:
        'While we strive for accuracy based on 2025 IRS rules, tax law is complex and individual circumstances vary.',
      notAdvice: 'Not Tax Advice:',
      notAdviceText:
        'This tool does not constitute tax, legal, or financial advice. We are not responsible for decisions made based on these estimates.',
      privacy: 'Privacy:',
      privacyText:
        'All data stays on your device. We do not collect or transmit personal tax information.',
      learnMore: 'Learn more about this tool',
      showLess: 'Show less',
      dismiss: 'Dismiss disclaimer',
    },
    onboarding: {
      welcome: {
        title: 'Welcome to USA Tax Calculator 2025',
        description:
          'This tool helps you estimate your federal and state income taxes. All calculations happen on your device - your data never leaves your computer.',
      },
      privacy: {
        title: 'Your Data Stays Private',
        description:
          'We take your privacy seriously. All tax calculations run locally in your browser. We never collect, store, or transmit your personal tax information.',
      },
      features: {
        title: 'Key Features',
        description:
          'Get accurate tax estimates with support for multiple income types, deductions, credits, and state taxes. Compare filing options and discover tax-saving opportunities.',
      },
      help: {
        title: 'Getting Help',
        description:
          'Look for the help icons (?) next to form fields for contextual guidance. The Tax Education Center provides detailed explanations of tax concepts.',
      },
      back: 'Back',
      next: 'Next',
      getStarted: 'Get Started',
      skip: 'Skip',
    },
    validation: {
      required: 'This field is required',
      invalidSSN: 'Please enter a valid SSN (XXX-XX-XXXX)',
      negativeAmount: 'Amount cannot be negative',
      tooLarge: 'Amount seems unreasonably large',
      invalidDependents: 'Number of dependents must be between 0 and 20',
    },
    tabs: {
      personal: 'Personal Info',
      income: 'Income',
      payments: 'Payments',
      deductions: 'Deductions',
    },
    personalInfo: {
      title: 'Personal Information',
      firstName: 'First Name',
      lastName: 'Last Name',
      ssn: 'Social Security Number',
      filingStatus: 'Filing Status',
      address: 'Address',
      dependents: 'Number of Dependents',
      marylandResident: 'Maryland Resident',
      county: 'Maryland County/City',
      filingStatuses: {
        single: 'Single',
        marriedJointly: 'Married Filing Jointly',
        marriedSeparately: 'Married Filing Separately',
        headOfHousehold: 'Head of Household',
      },
      placeholders: {
        firstName: 'Enter first name',
        lastName: 'Enter last name',
        ssn: 'XXX-XX-XXXX',
        address: 'Enter address',
      },
      help: {
        ssn: 'As shown on your Social Security card',
        dependents: 'Children and other qualifying relatives you support',
        filingStatus: 'Status as of December 31, 2025',
      },
    },
    spouseInfo: {
      title: 'Spouse Information',
      subtitle: 'Enter spouse information for joint filing',
      firstName: 'Spouse First Name',
      lastName: 'Spouse Last Name',
      ssn: 'Spouse SSN',
      income: 'Spouse Income',
      wages: 'Spouse Wages',
      withholding: 'Spouse Tax Withholding',
      addSpouse: 'Add Spouse Information',
      editSpouse: 'Edit Spouse Information',
      removeSpouse: 'Remove Spouse Information',
      filingComparison: 'Filing Status Comparison',
      recommendedFiling: 'Recommended Filing Status',
      jointSavings: 'Joint Filing Saves',
      separateSavings: 'Separate Filing Saves',
    },
    income: {
      title: 'Income Information',
      wages: 'Wages, Salaries, Tips (W-2)',
      interestIncome: 'Interest Income (1099-INT)',
      dividends: 'Dividend Income (1099-DIV)',
      capitalGains: 'Capital Gains',
      businessIncome: 'Business Income (Schedule C)',
      otherIncome: 'Other Income',
      k1Income: 'K-1 Partnership/S Corp Income',
      k1Section: {
        title: 'K-1 Forms (Partnership/S Corporation)',
        ordinaryIncome: 'Ordinary Business Income (Box 1)',
        netRentalRealEstate: 'Net Rental Real Estate (Box 2)',
        otherRentalIncome: 'Other Rental Income (Box 3)',
        guaranteedPayments: 'Guaranteed Payments (Box 4)',
        interestIncome: 'Interest Income (Box 5)',
        dividends: 'Ordinary Dividends (Box 6A)',
        royalties: 'Royalties (Box 7)',
        netShortTermCapitalGain: 'Net Short-term Capital Gain (Box 8A)',
        netLongTermCapitalGain: 'Net Long-term Capital Gain (Box 9A)',
        otherPortfolioIncome: 'Other Portfolio Income (Box 11)',
      },
      businessDetails: {
        title: 'Detailed Business Income (Schedule C)',
        grossReceipts: 'Gross Receipts or Sales',
        costOfGoodsSold: 'Cost of Goods Sold',
        grossProfit: 'Gross Profit',
        businessExpenses: 'Total Business Expenses',
        netBusinessIncome: 'Net Business Income',
      },
    },
    payments: {
      title: 'Tax Payments & Withholdings',
      federalWithholding: 'Federal Income Tax Withheld (W-2 Box 2)',
      stateWithholding: 'Maryland State Tax Withheld (W-2 Box 17)',
      estimatedTaxPayments: '2025 Estimated Tax Payments',
      priorYearOverpayment: 'Prior Year Overpayment Applied',
      otherPayments: 'Other Payments & Credits',
      paymentSummary: 'Payment Summary',
      totalPayments: 'Total Payments:',
      federalTaxOwed: 'Federal Tax Owed:',
      stateTaxOwed: 'State Tax Owed:',
      localTaxOwed: 'Local Tax Owed:',
      totalTaxOwed: 'Total Tax Owed:',
    },
    deductions: {
      title: 'Deductions',
      standardDeduction: 'Standard Deduction:',
      itemizeDeductions: 'Itemize Deductions',
      mortgageInterest: 'Mortgage Interest',
      stateLocalTaxes: 'State and Local Taxes (SALT) - Max $10,000',
      charitableContributions: 'Charitable Contributions',
      medicalExpenses: 'Medical Expenses (above 7.5% AGI)',
      otherItemized: 'Other Itemized Deductions',
    },
    credits: {
      title: 'Tax Credits',
      childCare: {
        title: 'Child and Dependent Care Credit',
        hasExpenses: 'I paid for childcare or dependent care',
        numberOfQualifying: 'Number of qualifying persons (under 13 or disabled)',
        expenses: 'Total care expenses paid',
        maxExpenses: 'Maximum: $3,000 for 1 person, $6,000 for 2 or more',
        help: 'Expenses for daycare, babysitters, before/after school programs, or day camps while you worked',
      },
      education: {
        title: 'Education Credits',
        hasExpenses: 'I or my dependents had qualified education expenses',
        studentName: 'Student name',
        tuitionAndFees: 'Tuition and fees paid',
        yearsOfPostSecondary: 'Years of post-secondary education',
        helpTuition: 'From Form 1098-T provided by your school',
        helpYears: 'American Opportunity Credit is limited to first 4 years',
        aotc: 'American Opportunity Credit',
        llc: 'Lifetime Learning Credit',
      },
    },
    results: {
      title: 'Tax Calculation (2025)',
      adjustedGrossIncome: 'Adjusted Gross Income:',
      federalTaxableIncome: 'Federal Taxable Income:',
      federalTax: 'Federal Tax:',
      marylandTax: 'Maryland State Tax:',
      localTax: 'Local Tax:',
      totalTax: 'Total Tax Owed:',
      totalPayments: 'Total Payments:',
      refundAmount: 'Refund Amount:',
      amountOwed: 'Amount You Owe:',
      effectiveRate: 'Effective Tax Rate:',
      marginalRate: 'Marginal Tax Rate:',
      afterTaxIncome: 'After-Tax Income:',
    },
    actions: {
      title: 'Actions',
      exportPDF: 'Export Form 1040 PDF',
      exportJSON: 'Export Tax Data (JSON)',
      recalculate: 'Recalculate',
      startWizard: 'Start Smart Tax Wizard',
      exitClassicMode: 'Exit Classic Mode',
      useClassicMode: 'Use Classic Mode',
      importExport: 'Import/Export',
      reviewCheck: 'Review & Check',
      quickInterview: 'Quick Interview',
      advanced: 'Advanced',
      aiHelp: 'AI Help',
      proMode: 'Pro Mode',
    },
    modernMode: {
      welcome: 'Welcome to Smart Tax Filing',
      welcomeDescription:
        "Our intelligent wizard guides you through your tax return step-by-step, ensuring you don't miss deductions and credits while keeping everything simple.",
      smartGuidance: 'Smart Guidance',
      smartGuidanceDesc: 'Contextual help and tips as you go',
      accuracyCheck: 'Accuracy Check',
      accuracyCheckDesc: 'Built-in validation and optimization',
      easyImport: 'Easy Import',
      easyImportDesc: 'Import from previous year or documents',
      startTaxReturn: 'Start Your Tax Return',
      importData: 'Import Data',
      importDataDesc: 'From last year or documents',
      reviewCheckTitle: 'Review & Check',
      reviewCheckDesc: 'Accuracy and optimization',
      advancedTools: 'Advanced Tools',
      advancedToolsDesc: 'Power features for complex returns',
      classicMode: 'Classic Mode',
      classicModeDesc: 'Jump to detailed tab-based entry',
      currentSnapshot: 'Current Tax Snapshot',
      refundBalanceDue: 'Refund / Balance Due',
      refund: 'Refund:',
      owe: 'Owe:',
      totalTax: 'Total Tax',
      effectiveRate: 'Effective Rate',
    },
    taxBrackets: {
      title: '2025 Federal Tax Brackets',
      taxableIncome: 'Taxable Income',
      rate: 'Rate',
    },
    marylandInfo: {
      title: 'Maryland Tax Info',
      stateRateRange: 'State Tax Rate Range:',
      localTaxRate: 'Local Tax Rate',
      standardDeduction: 'MD Standard Deduction:',
    },
    stateTaxSelector: {
      title: 'State Tax Calculator',
      statesSupported: 'states supported',
      selectState: 'Select Your State of Residence',
      chooseState: 'Choose a state...',
      taxInformation: 'Tax Information',
      showDetails: 'Show Details',
      hideDetails: 'Hide Details',
      estimatedTax: 'Estimated {state} Tax:',
      noStateTax: 'Great news! {state} has no state income tax.',
      taxType: 'Tax Type:',
      hasIncomeTax: 'Has Income Tax:',
      yes: 'Yes',
      no: 'No',
      taxBrackets: 'Tax Brackets',
      standardDeduction: 'Standard Deduction',
      personalExemption: 'Personal Exemption',
      additionalInfo: 'Additional Information',
      taxTypes: {
        flat: 'Flat Rate',
        progressive: 'Progressive',
        none: 'No Income Tax',
      },
    },
    standardDeductions: {
      title: '2025 Standard Deduction',
      single: 'Single:',
      marriedJointly: 'Married Filing Jointly:',
      marriedSeparately: 'Married Filing Separately:',
      headOfHousehold: 'Head of Household:',
    },
    assistant: {
      title: 'AI Tax Assistant',
      subtitle: 'Get instant answers to your tax questions',
      placeholder: 'Ask me anything about taxes...',
      greeting:
        "Hello! I'm your AI tax assistant. I can help you with tax questions, optimization strategies, and understanding your tax situation. What would you like to know?",
      suggestions: [
        'How can I reduce my tax burden?',
        'What deductions am I eligible for?',
        'Should I itemize or take the standard deduction?',
        'Explain my tax calculation',
      ],
      quickActions: [
        { label: 'Calculate Savings', query: 'How can I reduce my tax burden?' },
        { label: 'Deduction Help', query: 'What deductions am I eligible for?' },
        { label: 'Tax Strategy', query: 'What tax planning strategies should I consider?' },
        { label: 'General Tips', query: 'Give me your best tax tips' },
      ],
      tip: 'Tip: Ask about deductions, tax strategies, or specific tax scenarios',
    },
    backup: {
      manager: {
        title: 'Data Backup & Recovery',
        autoSave: {
          title: 'Auto-Save Protection',
          description: 'Automatically saves your data every 5 minutes to prevent data loss.',
        },
        security: {
          title: 'Data Security',
          tip1: 'All data is stored locally in your browser',
          tip2: 'Backups include checksums for data integrity verification',
          tip3: 'No personal information is sent to external servers',
          tip4: 'Export backups for additional security',
        },
        alert: {
          nameRequired: 'Please enter a backup name',
          success: 'Backup created successfully!',
          checksumMismatch: 'Backup data may be corrupted. Checksum mismatch.',
          restoreSuccess: 'Data restored successfully!',
          restoreError: 'Error restoring backup:',
          importSuccess: 'Backup imported successfully!',
          importError: 'Error importing backup:',
        },
      },
    },
    audit: {
      title: 'Audit Support & Documentation',
      documentation: 'Documentation',
      report: 'Report',
      checklist: 'Checklist',
      requiredDocuments: 'Required Documents',
      auditReport: 'Audit Report',
      preparationChecklist: 'Preparation Checklist',
      auditTips: 'Audit Preparation Tips',
      tips: {
        tip1: 'Keep all supporting documents organized and accessible',
        tip2: 'Maintain records for at least 3 years (7 years for certain situations)',
        tip3: 'Be prepared to explain any unusual deductions or income sources',
        tip4: 'Consider professional representation if audit is complex',
        tip5: 'Respond to IRS communications promptly and completely',
      },
    },
    auth: {
      // Login/Session
      secureAccess: 'Secure Access',
      passphraseDescription:
        'Enter a passphrase to encrypt your tax data. This creates a secure local session.',
      passphrase: 'Passphrase',
      enterPassphrase: 'Enter your passphrase',
      passphraseHint: 'Your passphrase encrypts data locally. We never see or store it.',
      passphraseRequired: 'Please enter a passphrase',
      localModeInfo: 'Your data is encrypted and stored only on this device. No account needed.',
      continue: 'Continue',
      loading: 'Please wait...',

      // Cloud login
      welcomeBack: 'Welcome Back',
      loginDescription: 'Sign in to access your saved tax data across devices.',
      email: 'Email Address',
      enterEmail: 'Enter your email',
      emailRequired: 'Please enter your email',
      invalidEmail: 'Please enter a valid email address',
      password: 'Password',
      enterPassword: 'Enter your password',
      passwordRequired: 'Please enter your password',
      login: 'Sign In',
      noAccount: "Don't have an account? Sign up",

      // Registration
      createAccount: 'Create Account',
      registerDescription: 'Sign up for cloud sync and access your data anywhere.',
      displayName: 'Display Name',
      enterDisplayName: 'How should we call you?',
      optional: 'optional',
      confirmPassword: 'Confirm Password',
      confirmPasswordPlaceholder: 'Enter password again',
      passwordTooShort: 'Password must be at least 8 characters',
      passwordMismatch: 'Passwords do not match',
      passwordRequirements: 'At least 8 characters',
      agreeToTerms: 'I agree to the',
      termsOfService: 'Terms of Service',
      and: 'and',
      privacyPolicy: 'Privacy Policy',
      mustAgreeToTerms: 'You must agree to the terms to continue',
      register: 'Create Account',
      alreadyHaveAccount: 'Already have an account? Sign in',

      // Account Settings
      localUser: 'Local User',
      localMode: 'Local Mode (Data stored on device)',
      cloudMode: 'Cloud Mode (Synced across devices)',
      profile: 'Profile',
      preferences: 'Preferences',
      security: 'Security',
      notSet: 'Not set',
      accountCreated: 'Account Created',
      lastLogin: 'Last Login',

      // Preferences
      theme: 'Theme',
      themeSystem: 'System Default',
      themeLight: 'Light',
      themeDark: 'Dark',
      autoSave: 'Auto-Save',
      autoSaveDescription: 'Automatically save changes as you type',
      autoBackup: 'Auto-Backup',
      autoBackupDescription: 'Create periodic backups of your data',
      showOnboarding: 'Show Welcome Guide',
      showOnboardingDescription: 'Show the welcome guide for new features',

      // Security
      cloudSync: 'Cloud Sync',
      cloudSyncDescription: 'Sync your encrypted data across devices',
      enableSyncTitle: 'Enable Cloud Sync?',
      enableSyncDescription:
        "Your data will be encrypted before upload. You'll need to create an account to use this feature.",
      enableSync: 'Enable Sync',
      endSession: 'End Session',
      logout: 'Sign Out',
    },
    common: {
      save: 'Save',
      cancel: 'Cancel',
      edit: 'Edit',
      close: 'Close',
      delete: 'Delete',
      confirm: 'Confirm',
    },
  },
  zh: {
    title: '美国税务计算器 2025',
    subtitle: '使用最新税率和扣除额估算您的2025年联邦和州所得税。',
    disclaimer: '仅供规划使用。非税务或法律建议。请咨询专业人士进行申报。',
    privacyNotice: '隐私声明：所有计算均在您的浏览器本地运行。数据不会发送到服务器。',
    disclaimerBanner: {
      title: '税务估算工具 - 非专业建议',
      summary:
        '本计算器仅提供估算结果，实际税务金额可能有所不同。准确报税请咨询专业税务人士或使用IRS认可的报税软件。',
      accuracy: '准确性：',
      accuracyText: '我们力求基于2025年IRS规则提供准确计算，但税法复杂，个人情况各异。',
      notAdvice: '非税务建议：',
      notAdviceText: '本工具不构成税务、法律或财务建议。我们对基于此估算做出的决定不承担责任。',
      privacy: '隐私保护：',
      privacyText: '所有数据保存在您的设备上。我们不收集或传输个人税务信息。',
      learnMore: '了解更多关于本工具',
      showLess: '收起',
      dismiss: '关闭提示',
    },
    onboarding: {
      welcome: {
        title: '欢迎使用美国税务计算器 2025',
        description:
          '本工具帮助您估算联邦和州所得税。所有计算在您的设备上进行 - 数据不会离开您的电脑。',
      },
      privacy: {
        title: '您的数据保持私密',
        description:
          '我们非常重视您的隐私。所有税务计算在浏览器本地运行。我们从不收集、存储或传输您的个人税务信息。',
      },
      features: {
        title: '主要功能',
        description: '支持多种收入类型、扣除额、税收抵免和州税计算。比较申报选项，发现节税机会。',
      },
      help: {
        title: '获取帮助',
        description:
          '表单字段旁边有帮助图标(?)，可查看相关指导。税务教育中心提供详细的税务概念说明。',
      },
      back: '上一步',
      next: '下一步',
      getStarted: '开始使用',
      skip: '跳过',
    },
    validation: {
      required: '此字段为必填项',
      invalidSSN: '请输入有效的社会保险号 (XXX-XX-XXXX)',
      negativeAmount: '金额不能为负数',
      tooLarge: '金额似乎过大',
      invalidDependents: '受抚养人数必须在0到20之间',
    },
    tabs: {
      personal: '个人信息',
      income: '收入',
      payments: '付款',
      deductions: '扣除额',
    },
    personalInfo: {
      title: '个人信息',
      firstName: '名字',
      lastName: '姓氏',
      ssn: '社会保险号',
      filingStatus: '申报状态',
      address: '地址',
      dependents: '受抚养人数',
      marylandResident: '马里兰州居民',
      county: '马里兰县/市',
      filingStatuses: {
        single: '单身',
        marriedJointly: '已婚联合申报',
        marriedSeparately: '已婚分别申报',
        headOfHousehold: '户主',
      },
      placeholders: {
        firstName: '输入名字',
        lastName: '输入姓氏',
        ssn: 'XXX-XX-XXXX',
        address: '输入地址',
      },
      help: {
        ssn: '如您社会保险卡上显示的',
        dependents: '您抚养的子女和其他符合条件的亲属',
        filingStatus: '截至2025年12月31日的状态',
      },
    },
    spouseInfo: {
      title: '配偶信息',
      subtitle: '输入配偶信息以进行联合申报',
      firstName: '配偶名字',
      lastName: '配偶姓氏',
      ssn: '配偶社会保险号',
      income: '配偶收入',
      wages: '配偶工资',
      withholding: '配偶税务预扣',
      addSpouse: '添加配偶信息',
      editSpouse: '编辑配偶信息',
      removeSpouse: '删除配偶信息',
      filingComparison: '申报状态比较',
      recommendedFiling: '推荐申报状态',
      jointSavings: '联合申报节省',
      separateSavings: '分别申报节省',
    },
    income: {
      title: '收入信息',
      wages: '工资、薪金、小费 (W-2)',
      interestIncome: '利息收入 (1099-INT)',
      dividends: '股息收入 (1099-DIV)',
      capitalGains: '资本收益',
      businessIncome: '商业收入 (附表C)',
      otherIncome: '其他收入',
      k1Income: 'K-1合伙企业/S型公司收入',
      k1Section: {
        title: 'K-1表格 (合伙企业/S型公司)',
        ordinaryIncome: '普通商业收入 (第1栏)',
        netRentalRealEstate: '净租赁房地产 (第2栏)',
        otherRentalIncome: '其他租赁收入 (第3栏)',
        guaranteedPayments: '保证付款 (第4栏)',
        interestIncome: '利息收入 (第5栏)',
        dividends: '普通股息 (第6A栏)',
        royalties: '特许权使用费 (第7栏)',
        netShortTermCapitalGain: '净短期资本收益 (第8A栏)',
        netLongTermCapitalGain: '净长期资本收益 (第9A栏)',
        otherPortfolioIncome: '其他投资组合收入 (第11栏)',
      },
      businessDetails: {
        title: '详细商业收入 (附表C)',
        grossReceipts: '总收入或销售额',
        costOfGoodsSold: '销售成本',
        grossProfit: '毛利润',
        businessExpenses: '商业费用总额',
        netBusinessIncome: '净商业收入',
      },
    },
    payments: {
      title: '税务付款和预扣',
      federalWithholding: '联邦所得税预扣 (W-2第2栏)',
      stateWithholding: '马里兰州税预扣 (W-2第17栏)',
      estimatedTaxPayments: '2025年预估税款',
      priorYearOverpayment: '前一年度多付款项',
      otherPayments: '其他付款和抵免',
      paymentSummary: '付款摘要',
      totalPayments: '总付款额：',
      federalTaxOwed: '联邦税欠款：',
      stateTaxOwed: '州税欠款：',
      localTaxOwed: '地方税欠款：',
      totalTaxOwed: '总税款欠额：',
    },
    deductions: {
      title: '扣除额',
      standardDeduction: '标准扣除额：',
      itemizeDeductions: '逐项扣除',
      mortgageInterest: '抵押贷款利息',
      stateLocalTaxes: '州和地方税 (SALT) - 最高$10,000',
      charitableContributions: '慈善捐款',
      medicalExpenses: '医疗费用 (超过AGI的7.5%)',
      otherItemized: '其他逐项扣除',
    },
    credits: {
      title: '税收抵免',
      childCare: {
        title: '儿童和受抚养人照顾抵免',
        hasExpenses: '我支付了儿童照顾或受抚养人照顾费用',
        numberOfQualifying: '符合条件的人数（13岁以下或残疾）',
        expenses: '支付的照顾费用总额',
        maxExpenses: '最高：1人$3,000，2人或以上$6,000',
        help: '工作期间的托儿所、保姆、课前课后项目或日间夏令营费用',
      },
      education: {
        title: '教育抵免',
        hasExpenses: '我或我的受抚养人有合格的教育费用',
        studentName: '学生姓名',
        tuitionAndFees: '支付的学费和费用',
        yearsOfPostSecondary: '完成的高等教育年数',
        helpTuition: '来自学校提供的1098-T表格',
        helpYears: '美国机会抵免仅限于前4年',
        aotc: '美国机会抵免',
        llc: '终身学习抵免',
      },
    },
    results: {
      title: '税务计算 (2025)',
      adjustedGrossIncome: '调整后总收入：',
      federalTaxableIncome: '联邦应税收入：',
      federalTax: '联邦税：',
      marylandTax: '马里兰州税：',
      localTax: '地方税：',
      totalTax: '总税款：',
      totalPayments: '总付款额：',
      refundAmount: '退款金额：',
      amountOwed: '您应缴金额：',
      effectiveRate: '有效税率：',
      marginalRate: '边际税率：',
      afterTaxIncome: '税后收入：',
    },
    actions: {
      title: '操作',
      exportPDF: '导出1040表格PDF',
      exportJSON: '导出税务数据 (JSON)',
      recalculate: '重新计算',
      startWizard: '启动智能税务向导',
      exitClassicMode: '退出经典模式',
      useClassicMode: '使用经典模式',
      importExport: '导入/导出',
      reviewCheck: '审核检查',
      quickInterview: '快速问答',
      advanced: '高级功能',
      aiHelp: 'AI 帮助',
      proMode: '专业模式',
    },
    modernMode: {
      welcome: '欢迎使用智能税务申报',
      welcomeDescription:
        '我们的智能向导将逐步引导您完成税务申报，确保您不会错过任何扣除额和抵免额，同时让一切保持简单。',
      smartGuidance: '智能指导',
      smartGuidanceDesc: '随时提供上下文帮助和提示',
      accuracyCheck: '准确性检查',
      accuracyCheckDesc: '内置验证和优化功能',
      easyImport: '便捷导入',
      easyImportDesc: '从去年或文档导入',
      startTaxReturn: '开始税务申报',
      importData: '导入数据',
      importDataDesc: '从去年或文档导入',
      reviewCheckTitle: '审核检查',
      reviewCheckDesc: '准确性和优化',
      advancedTools: '高级工具',
      advancedToolsDesc: '复杂申报的强大功能',
      classicMode: '经典模式',
      classicModeDesc: '跳转到详细的标签式输入',
      currentSnapshot: '当前税务概况',
      refundBalanceDue: '退款/应缴金额',
      refund: '退款：',
      owe: '应缴：',
      totalTax: '总税款',
      effectiveRate: '有效税率',
    },
    taxBrackets: {
      title: '2025年联邦税率级距',
      taxableIncome: '应税收入',
      rate: '税率',
    },
    marylandInfo: {
      title: '马里兰税务信息',
      stateRateRange: '州税率范围：',
      localTaxRate: '地方税率',
      standardDeduction: 'MD标准扣除额：',
    },
    stateTaxSelector: {
      title: '州税计算器',
      statesSupported: '个州已支持',
      selectState: '选择您的居住州',
      chooseState: '请选择州...',
      taxInformation: '税务信息',
      showDetails: '显示详情',
      hideDetails: '隐藏详情',
      estimatedTax: '预计{state}州税：',
      noStateTax: '好消息！{state}没有州所得税。',
      taxType: '税务类型：',
      hasIncomeTax: '是否有所得税：',
      yes: '是',
      no: '否',
      taxBrackets: '税率级距',
      standardDeduction: '标准扣除额',
      personalExemption: '个人免税额',
      additionalInfo: '附加信息',
      taxTypes: {
        flat: '单一税率',
        progressive: '累进税率',
        none: '无所得税',
      },
    },
    standardDeductions: {
      title: '2025年标准扣除额',
      single: '单身：',
      marriedJointly: '已婚联合申报：',
      marriedSeparately: '已婚分别申报：',
      headOfHousehold: '户主：',
    },
    assistant: {
      title: 'AI税务助手',
      subtitle: '获得税务问题的即时解答',
      placeholder: '询问我任何税务问题...',
      greeting:
        '您好！我是您的AI税务助手。我可以帮助您解答税务问题、优化策略，并了解您的税务状况。您想了解什么？',
      suggestions: [
        '如何减少我的税务负担？',
        '我符合哪些扣除条件？',
        '我应该逐项扣除还是采用标准扣除？',
        '解释我的税务计算',
      ],
      quickActions: [
        { label: '计算节省', query: '如何减少我的税务负担？' },
        { label: '扣除帮助', query: '我符合哪些扣除条件？' },
        { label: '税务策略', query: '我应该考虑哪些税务规划策略？' },
        { label: '一般建议', query: '给我您最好的税务建议' },
      ],
      tip: '提示：询问扣除、税务策略或具体税务情况',
    },
    backup: {
      manager: {
        title: '数据备份与恢复',
        autoSave: {
          title: '自动保存保护',
          description: '每5分钟自动保存您的数据以防止数据丢失。',
        },
        security: {
          title: '数据安全',
          tip1: '所有数据都在您的浏览器本地存储',
          tip2: '备份包含校验和以验证数据完整性',
          tip3: '不会向外部服务器发送个人信息',
          tip4: '导出备份以获得额外安全保障',
        },
        alert: {
          nameRequired: '请输入备份名称',
          success: '备份创建成功！',
          checksumMismatch: '备份数据可能已损坏。校验和不匹配。',
          restoreSuccess: '数据恢复成功！',
          restoreError: '恢复备份时出错：',
          importSuccess: '备份导入成功！',
          importError: '导入备份时出错：',
        },
      },
    },
    audit: {
      title: '审计支持与文档',
      documentation: '文档',
      report: '报告',
      checklist: '检查清单',
      requiredDocuments: '所需文档',
      auditReport: '审计报告',
      preparationChecklist: '准备检查清单',
      auditTips: '审计准备提示',
      tips: {
        tip1: '保持所有支持文档的整理和可访问性',
        tip2: '至少保留3年的记录（某些情况下需保留7年）',
        tip3: '准备好解释任何异常的扣除或收入来源',
        tip4: '如果审计复杂，考虑专业代表',
        tip5: '及时、完整地回应IRS的通信',
      },
    },
    auth: {
      // 登录/会话
      secureAccess: '安全访问',
      passphraseDescription: '输入密码短语以加密您的税务数据。这将创建一个安全的本地会话。',
      passphrase: '密码短语',
      enterPassphrase: '输入您的密码短语',
      passphraseHint: '您的密码短语在本地加密数据。我们不会看到或存储它。',
      passphraseRequired: '请输入密码短语',
      localModeInfo: '您的数据已加密并仅存储在此设备上。无需账户。',
      continue: '继续',
      loading: '请稍候...',

      // 云端登录
      welcomeBack: '欢迎回来',
      loginDescription: '登录以跨设备访问您保存的税务数据。',
      email: '电子邮箱',
      enterEmail: '输入您的邮箱',
      emailRequired: '请输入您的邮箱',
      invalidEmail: '请输入有效的邮箱地址',
      password: '密码',
      enterPassword: '输入您的密码',
      passwordRequired: '请输入您的密码',
      login: '登录',
      noAccount: '没有账户？立即注册',

      // 注册
      createAccount: '创建账户',
      registerDescription: '注册以启用云同步，随时随地访问您的数据。',
      displayName: '显示名称',
      enterDisplayName: '您希望我们如何称呼您？',
      optional: '可选',
      confirmPassword: '确认密码',
      confirmPasswordPlaceholder: '再次输入密码',
      passwordTooShort: '密码至少需要8个字符',
      passwordMismatch: '两次输入的密码不一致',
      passwordRequirements: '至少8个字符',
      agreeToTerms: '我同意',
      termsOfService: '服务条款',
      and: '和',
      privacyPolicy: '隐私政策',
      mustAgreeToTerms: '您必须同意条款才能继续',
      register: '创建账户',
      alreadyHaveAccount: '已有账户？立即登录',

      // 账户设置
      localUser: '本地用户',
      localMode: '本地模式（数据存储在设备上）',
      cloudMode: '云端模式（跨设备同步）',
      profile: '个人资料',
      preferences: '偏好设置',
      security: '安全',
      notSet: '未设置',
      accountCreated: '账户创建于',
      lastLogin: '上次登录',

      // 偏好设置
      theme: '主题',
      themeSystem: '跟随系统',
      themeLight: '浅色',
      themeDark: '深色',
      autoSave: '自动保存',
      autoSaveDescription: '输入时自动保存更改',
      autoBackup: '自动备份',
      autoBackupDescription: '定期创建数据备份',
      showOnboarding: '显示欢迎指南',
      showOnboardingDescription: '显示新功能的欢迎指南',

      // 安全
      cloudSync: '云端同步',
      cloudSyncDescription: '跨设备同步您的加密数据',
      enableSyncTitle: '启用云端同步？',
      enableSyncDescription: '您的数据将在上传前加密。您需要创建账户才能使用此功能。',
      enableSync: '启用同步',
      endSession: '结束会话',
      logout: '退出登录',
    },
    common: {
      save: '保存',
      cancel: '取消',
      edit: '编辑',
      close: '关闭',
      delete: '删除',
      confirm: '确认',
    },
  },
  es: {
    title: 'Calculadora de Impuestos de EE.UU. 2025',
    subtitle:
      'Estime sus impuestos federales y estatales para 2025 con tasas y deducciones actualizadas.',
    disclaimer:
      'Solo para planificación. No es asesoramiento fiscal o legal. Consulte a un profesional para la presentación.',
    privacyNotice:
      'Aviso de Privacidad: Todos los cálculos se ejecutan localmente en su navegador. No se envían datos a un servidor.',
    disclaimerBanner: {
      title: 'Herramienta de Estimación de Impuestos - No es Asesoría Profesional',
      summary:
        'Esta calculadora proporciona solo estimaciones. Los resultados pueden diferir de su obligación tributaria real. Para una declaración precisa, consulte a un profesional de impuestos calificado o use software aprobado por el IRS.',
      accuracy: 'Precisión:',
      accuracyText:
        'Aunque nos esforzamos por la precisión basada en las reglas del IRS 2025, la ley tributaria es compleja y las circunstancias individuales varían.',
      notAdvice: 'No es Asesoría Fiscal:',
      notAdviceText:
        'Esta herramienta no constituye asesoramiento fiscal, legal o financiero. No somos responsables de las decisiones tomadas basadas en estas estimaciones.',
      privacy: 'Privacidad:',
      privacyText:
        'Todos los datos permanecen en su dispositivo. No recopilamos ni transmitimos información fiscal personal.',
      learnMore: 'Más información sobre esta herramienta',
      showLess: 'Mostrar menos',
      dismiss: 'Cerrar aviso',
    },
    onboarding: {
      welcome: {
        title: 'Bienvenido a la Calculadora de Impuestos de EE.UU. 2025',
        description:
          'Esta herramienta le ayuda a estimar sus impuestos federales y estatales. Todos los cálculos se realizan en su dispositivo - sus datos nunca salen de su computadora.',
      },
      privacy: {
        title: 'Sus Datos Permanecen Privados',
        description:
          'Tomamos su privacidad en serio. Todos los cálculos de impuestos se ejecutan localmente en su navegador. Nunca recopilamos, almacenamos ni transmitimos su información fiscal personal.',
      },
      features: {
        title: 'Características Principales',
        description:
          'Obtenga estimaciones precisas con soporte para múltiples tipos de ingresos, deducciones, créditos e impuestos estatales. Compare opciones de declaración y descubra oportunidades de ahorro fiscal.',
      },
      help: {
        title: 'Obtener Ayuda',
        description:
          'Busque los iconos de ayuda (?) junto a los campos del formulario para obtener orientación contextual. El Centro de Educación Fiscal proporciona explicaciones detalladas de conceptos fiscales.',
      },
      back: 'Atrás',
      next: 'Siguiente',
      getStarted: 'Comenzar',
      skip: 'Omitir',
    },
    validation: {
      required: 'Este campo es obligatorio',
      invalidSSN: 'Por favor ingrese un SSN válido (XXX-XX-XXXX)',
      negativeAmount: 'La cantidad no puede ser negativa',
      tooLarge: 'La cantidad parece excesivamente grande',
      invalidDependents: 'El número de dependientes debe estar entre 0 y 20',
    },
    tabs: {
      personal: 'Información Personal',
      income: 'Ingresos',
      payments: 'Pagos',
      deductions: 'Deducciones',
    },
    personalInfo: {
      title: 'Información Personal',
      firstName: 'Nombre',
      lastName: 'Apellido',
      ssn: 'Número de Seguro Social',
      filingStatus: 'Estado Civil',
      address: 'Dirección',
      dependents: 'Número de Dependientes',
      marylandResident: 'Residente de Maryland',
      county: 'Condado/Ciudad de Maryland',
      filingStatuses: {
        single: 'Soltero',
        marriedJointly: 'Casado Presentando Conjuntamente',
        marriedSeparately: 'Casado Presentando por Separado',
        headOfHousehold: 'Jefe de Familia',
      },
      placeholders: {
        firstName: 'Ingrese el nombre',
        lastName: 'Ingrese el apellido',
        ssn: 'XXX-XX-XXXX',
        address: 'Ingrese la dirección',
      },
      help: {
        ssn: 'Como aparece en su tarjeta de Seguro Social',
        dependents: 'Hijos y otros parientes calificados que mantiene',
        filingStatus: 'Estado al 31 de diciembre de 2025',
      },
    },
    spouseInfo: {
      title: 'Información del Cónyuge',
      subtitle: 'Ingrese la información del cónyuge para presentación conjunta',
      firstName: 'Nombre del Cónyuge',
      lastName: 'Apellido del Cónyuge',
      ssn: 'SSN del Cónyuge',
      income: 'Ingresos del Cónyuge',
      wages: 'Salarios del Cónyuge',
      withholding: 'Retención de Impuestos del Cónyuge',
      addSpouse: 'Agregar Información del Cónyuge',
      editSpouse: 'Editar Información del Cónyuge',
      removeSpouse: 'Eliminar Información del Cónyuge',
      filingComparison: 'Comparación de Estado de Presentación',
      recommendedFiling: 'Estado de Presentación Recomendado',
      jointSavings: 'Presentación Conjunta Ahorra',
      separateSavings: 'Presentación Separada Ahorra',
    },
    income: {
      title: 'Información de Ingresos',
      wages: 'Salarios, Sueldos, Propinas (W-2)',
      interestIncome: 'Ingresos por Intereses (1099-INT)',
      dividends: 'Ingresos por Dividendos (1099-DIV)',
      capitalGains: 'Ganancias de Capital',
      businessIncome: 'Ingresos de Negocio (Anexo C)',
      otherIncome: 'Otros Ingresos',
      k1Income: 'Ingresos K-1 Sociedad/Corporación S',
      k1Section: {
        title: 'Formularios K-1 (Sociedad/Corporación S)',
        ordinaryIncome: 'Ingresos Ordinarios de Negocio (Casilla 1)',
        netRentalRealEstate: 'Bienes Raíces de Alquiler Neto (Casilla 2)',
        otherRentalIncome: 'Otros Ingresos de Alquiler (Casilla 3)',
        guaranteedPayments: 'Pagos Garantizados (Casilla 4)',
        interestIncome: 'Ingresos por Intereses (Casilla 5)',
        dividends: 'Dividendos Ordinarios (Casilla 6A)',
        royalties: 'Regalías (Casilla 7)',
        netShortTermCapitalGain: 'Ganancia de Capital a Corto Plazo Neta (Casilla 8A)',
        netLongTermCapitalGain: 'Ganancia de Capital a Largo Plazo Neta (Casilla 9A)',
        otherPortfolioIncome: 'Otros Ingresos de Cartera (Casilla 11)',
      },
      businessDetails: {
        title: 'Ingresos Detallados del Negocio (Anexo C)',
        grossReceipts: 'Recibos Brutos o Ventas',
        costOfGoodsSold: 'Costo de Bienes Vendidos',
        grossProfit: 'Ganancia Bruta',
        businessExpenses: 'Gastos Totales del Negocio',
        netBusinessIncome: 'Ingresos Netos del Negocio',
      },
    },
    payments: {
      title: 'Pagos de Impuestos y Retenciones',
      federalWithholding: 'Impuesto Federal Retenido (W-2 Casilla 2)',
      stateWithholding: 'Impuesto Estatal de Maryland Retenido (W-2 Casilla 17)',
      estimatedTaxPayments: 'Pagos de Impuestos Estimados 2025',
      priorYearOverpayment: 'Sobrepago del Año Anterior Aplicado',
      otherPayments: 'Otros Pagos y Créditos',
      paymentSummary: 'Resumen de Pagos',
      totalPayments: 'Pagos Totales:',
      federalTaxOwed: 'Impuesto Federal Adeudado:',
      stateTaxOwed: 'Impuesto Estatal Adeudado:',
      localTaxOwed: 'Impuesto Local Adeudado:',
      totalTaxOwed: 'Impuesto Total Adeudado:',
    },
    deductions: {
      title: 'Deducciones',
      standardDeduction: 'Deducción Estándar:',
      itemizeDeductions: 'Detallar Deducciones',
      mortgageInterest: 'Intereses Hipotecarios',
      stateLocalTaxes: 'Impuestos Estatales y Locales (SALT) - Máx $10,000',
      charitableContributions: 'Contribuciones Caritativas',
      medicalExpenses: 'Gastos Médicos (por encima del 7.5% AGI)',
      otherItemized: 'Otras Deducciones Detalladas',
    },
    credits: {
      title: 'Créditos Fiscales',
      childCare: {
        title: 'Crédito por Cuidado de Niños y Dependientes',
        hasExpenses: 'Pagué por cuidado de niños o dependientes',
        numberOfQualifying: 'Número de personas calificadas (menores de 13 o discapacitados)',
        expenses: 'Total de gastos de cuidado pagados',
        maxExpenses: 'Máximo: $3,000 para 1 persona, $6,000 para 2 o más',
        help: 'Gastos de guardería, niñeras, programas antes/después de la escuela o campamentos diurnos mientras trabajaba',
      },
      education: {
        title: 'Créditos de Educación',
        hasExpenses: 'Yo o mis dependientes tuvimos gastos de educación calificados',
        studentName: 'Nombre del estudiante',
        tuitionAndFees: 'Matrícula y cuotas pagadas',
        yearsOfPostSecondary: 'Años de educación postsecundaria',
        helpTuition: 'Del formulario 1098-T proporcionado por su escuela',
        helpYears: 'El Crédito de Oportunidad Americana está limitado a los primeros 4 años',
        aotc: 'Crédito de Oportunidad Americana',
        llc: 'Crédito de Aprendizaje de Por Vida',
      },
    },
    results: {
      title: 'Cálculo de Impuestos (2025)',
      adjustedGrossIncome: 'Ingreso Bruto Ajustado:',
      federalTaxableIncome: 'Ingreso Gravable Federal:',
      federalTax: 'Impuesto Federal:',
      marylandTax: 'Impuesto Estatal de Maryland:',
      localTax: 'Impuesto Local:',
      totalTax: 'Impuesto Total Adeudado:',
      totalPayments: 'Pagos Totales:',
      refundAmount: 'Cantidad de Reembolso:',
      amountOwed: 'Cantidad que Debe:',
      effectiveRate: 'Tasa de Impuesto Efectiva:',
      marginalRate: 'Tasa de Impuesto Marginal:',
      afterTaxIncome: 'Ingreso Después de Impuestos:',
    },
    actions: {
      title: 'Acciones',
      exportPDF: 'Exportar Formulario 1040 PDF',
      exportJSON: 'Exportar Datos de Impuestos (JSON)',
      recalculate: 'Recalcular',
      startWizard: 'Iniciar Asistente Inteligente',
      exitClassicMode: 'Salir del Modo Clásico',
      useClassicMode: 'Usar Modo Clásico',
      importExport: 'Importar/Exportar',
      reviewCheck: 'Revisar y Verificar',
      quickInterview: 'Entrevista Rápida',
      advanced: 'Avanzado',
      aiHelp: 'Ayuda IA',
      proMode: 'Modo Profesional',
    },
    modernMode: {
      welcome: 'Bienvenido a la Declaración de Impuestos Inteligente',
      welcomeDescription:
        'Nuestro asistente inteligente lo guiará paso a paso en su declaración de impuestos, asegurándose de que no se pierda deducciones y créditos mientras mantiene todo simple.',
      smartGuidance: 'Guía Inteligente',
      smartGuidanceDesc: 'Ayuda contextual y consejos sobre la marcha',
      accuracyCheck: 'Verificación de Precisión',
      accuracyCheckDesc: 'Validación y optimización integradas',
      easyImport: 'Importación Fácil',
      easyImportDesc: 'Importar del año anterior o documentos',
      startTaxReturn: 'Comenzar su Declaración de Impuestos',
      importData: 'Importar Datos',
      importDataDesc: 'Del año pasado o documentos',
      reviewCheckTitle: 'Revisar y Verificar',
      reviewCheckDesc: 'Precisión y optimización',
      advancedTools: 'Herramientas Avanzadas',
      advancedToolsDesc: 'Funciones potentes para declaraciones complejas',
      classicMode: 'Modo Clásico',
      classicModeDesc: 'Saltar a la entrada detallada basada en pestañas',
      currentSnapshot: 'Resumen Fiscal Actual',
      refundBalanceDue: 'Reembolso / Saldo Adeudado',
      refund: 'Reembolso:',
      owe: 'Debe:',
      totalTax: 'Impuesto Total',
      effectiveRate: 'Tasa Efectiva',
    },
    taxBrackets: {
      title: 'Tramos de Impuestos Federales 2025',
      taxableIncome: 'Ingreso Gravable',
      rate: 'Tasa',
    },
    marylandInfo: {
      title: 'Información de Impuestos de Maryland',
      stateRateRange: 'Rango de Tasa Estatal:',
      localTaxRate: 'Tasa de Impuesto Local',
      standardDeduction: 'Deducción Estándar MD:',
    },
    stateTaxSelector: {
      title: 'Calculadora de Impuestos Estatales',
      statesSupported: 'estados admitidos',
      selectState: 'Seleccione su Estado de Residencia',
      chooseState: 'Elija un estado...',
      taxInformation: 'Información Fiscal',
      showDetails: 'Mostrar Detalles',
      hideDetails: 'Ocultar Detalles',
      estimatedTax: 'Impuesto Estimado de {state}:',
      noStateTax: '¡Buenas noticias! {state} no tiene impuesto sobre la renta estatal.',
      taxType: 'Tipo de Impuesto:',
      hasIncomeTax: 'Tiene Impuesto sobre la Renta:',
      yes: 'Sí',
      no: 'No',
      taxBrackets: 'Tramos de Impuestos',
      standardDeduction: 'Deducción Estándar',
      personalExemption: 'Exención Personal',
      additionalInfo: 'Información Adicional',
      taxTypes: {
        flat: 'Tasa Plana',
        progressive: 'Progresivo',
        none: 'Sin Impuesto sobre la Renta',
      },
    },
    standardDeductions: {
      title: 'Deducción Estándar 2025',
      single: 'Soltero:',
      marriedJointly: 'Casado Presentando Conjuntamente:',
      marriedSeparately: 'Casado Presentando por Separado:',
      headOfHousehold: 'Jefe de Familia:',
    },
    assistant: {
      title: 'Asistente Fiscal AI',
      subtitle: 'Obtenga respuestas instantáneas a sus preguntas fiscales',
      placeholder: 'Pregúnteme cualquier cosa sobre impuestos...',
      greeting:
        '¡Hola! Soy su asistente fiscal de IA. Puedo ayudarle con preguntas fiscales, estrategias de optimización y entender su situación fiscal. ¿Qué le gustaría saber?',
      suggestions: [
        '¿Cómo puedo reducir mi carga fiscal?',
        '¿Para qué deducciones soy elegible?',
        '¿Debería detallar o tomar la deducción estándar?',
        'Explique mi cálculo de impuestos',
      ],
      quickActions: [
        { label: 'Calcular Ahorros', query: '¿Cómo puedo reducir mi carga fiscal?' },
        { label: 'Ayuda Deducciones', query: '¿Para qué deducciones soy elegible?' },
        {
          label: 'Estrategia Fiscal',
          query: '¿Qué estrategias de planificación fiscal debería considerar?',
        },
        { label: 'Consejos Generales', query: 'Deme sus mejores consejos fiscales' },
      ],
      tip: 'Consejo: Pregunte sobre deducciones, estrategias fiscales o escenarios fiscales específicos',
    },
    backup: {
      manager: {
        title: 'Copia de Seguridad y Recuperación de Datos',
        autoSave: {
          title: 'Protección de Guardado Automático',
          description:
            'Guarda automáticamente sus datos cada 5 minutos para evitar la pérdida de datos.',
        },
        security: {
          title: 'Seguridad de Datos',
          tip1: 'Todos los datos se almacenan localmente en su navegador',
          tip2: 'Las copias de seguridad incluyen sumas de verificación para la verificación de integridad de datos',
          tip3: 'No se envía información personal a servidores externos',
          tip4: 'Exporte copias de seguridad para seguridad adicional',
        },
        alert: {
          nameRequired: 'Por favor ingrese un nombre de copia de seguridad',
          success: '¡Copia de seguridad creada exitosamente!',
          checksumMismatch:
            'Los datos de la copia de seguridad pueden estar corruptos. No coinciden las sumas de verificación.',
          restoreSuccess: '¡Datos restaurados exitosamente!',
          restoreError: 'Error al restaurar la copia de seguridad:',
          importSuccess: '¡Copia de seguridad importada exitosamente!',
          importError: 'Error al importar la copia de seguridad:',
        },
      },
    },
    audit: {
      title: 'Soporte de Auditoría y Documentación',
      documentation: 'Documentación',
      report: 'Informe',
      checklist: 'Lista de Verificación',
      requiredDocuments: 'Documentos Requeridos',
      auditReport: 'Informe de Auditoría',
      preparationChecklist: 'Lista de Verificación de Preparación',
      auditTips: 'Consejos de Preparación para Auditoría',
      tips: {
        tip1: 'Mantenga todos los documentos de respaldo organizados y accesibles',
        tip2: 'Mantenga registros durante al menos 3 años (7 años para ciertas situaciones)',
        tip3: 'Esté preparado para explicar cualquier deducción o fuente de ingresos inusual',
        tip4: 'Considere representación profesional si la auditoría es compleja',
        tip5: 'Responda a las comunicaciones del IRS de manera oportuna y completa',
      },
    },
    auth: {
      // Inicio de sesión
      secureAccess: 'Acceso Seguro',
      passphraseDescription:
        'Ingrese una frase de contraseña para cifrar sus datos fiscales. Esto crea una sesión local segura.',
      passphrase: 'Frase de Contraseña',
      enterPassphrase: 'Ingrese su frase de contraseña',
      passphraseHint:
        'Su frase de contraseña cifra los datos localmente. Nunca la vemos ni la almacenamos.',
      passphraseRequired: 'Por favor ingrese una frase de contraseña',
      localModeInfo:
        'Sus datos están cifrados y almacenados solo en este dispositivo. No se necesita cuenta.',
      continue: 'Continuar',
      loading: 'Por favor espere...',

      // Inicio de sesión en la nube
      welcomeBack: 'Bienvenido de Nuevo',
      loginDescription:
        'Inicie sesión para acceder a sus datos fiscales guardados en todos sus dispositivos.',
      email: 'Correo Electrónico',
      enterEmail: 'Ingrese su correo electrónico',
      emailRequired: 'Por favor ingrese su correo electrónico',
      invalidEmail: 'Por favor ingrese un correo electrónico válido',
      password: 'Contraseña',
      enterPassword: 'Ingrese su contraseña',
      passwordRequired: 'Por favor ingrese su contraseña',
      login: 'Iniciar Sesión',
      noAccount: '¿No tiene cuenta? Regístrese',

      // Registro
      createAccount: 'Crear Cuenta',
      registerDescription:
        'Regístrese para sincronización en la nube y acceda a sus datos desde cualquier lugar.',
      displayName: 'Nombre para Mostrar',
      enterDisplayName: '¿Cómo debemos llamarle?',
      optional: 'opcional',
      confirmPassword: 'Confirmar Contraseña',
      confirmPasswordPlaceholder: 'Ingrese la contraseña nuevamente',
      passwordTooShort: 'La contraseña debe tener al menos 8 caracteres',
      passwordMismatch: 'Las contraseñas no coinciden',
      passwordRequirements: 'Al menos 8 caracteres',
      agreeToTerms: 'Acepto los',
      termsOfService: 'Términos de Servicio',
      and: 'y',
      privacyPolicy: 'Política de Privacidad',
      mustAgreeToTerms: 'Debe aceptar los términos para continuar',
      register: 'Crear Cuenta',
      alreadyHaveAccount: '¿Ya tiene cuenta? Inicie sesión',

      // Configuración de cuenta
      localUser: 'Usuario Local',
      localMode: 'Modo Local (Datos almacenados en el dispositivo)',
      cloudMode: 'Modo Nube (Sincronizado entre dispositivos)',
      profile: 'Perfil',
      preferences: 'Preferencias',
      security: 'Seguridad',
      notSet: 'No establecido',
      accountCreated: 'Cuenta Creada',
      lastLogin: 'Último Inicio de Sesión',

      // Preferencias
      theme: 'Tema',
      themeSystem: 'Predeterminado del Sistema',
      themeLight: 'Claro',
      themeDark: 'Oscuro',
      autoSave: 'Guardado Automático',
      autoSaveDescription: 'Guardar cambios automáticamente mientras escribe',
      autoBackup: 'Respaldo Automático',
      autoBackupDescription: 'Crear respaldos periódicos de sus datos',
      showOnboarding: 'Mostrar Guía de Bienvenida',
      showOnboardingDescription: 'Mostrar la guía de bienvenida para nuevas funciones',

      // Seguridad
      cloudSync: 'Sincronización en la Nube',
      cloudSyncDescription: 'Sincronizar sus datos cifrados entre dispositivos',
      enableSyncTitle: '¿Habilitar Sincronización en la Nube?',
      enableSyncDescription:
        'Sus datos serán cifrados antes de subirse. Necesitará crear una cuenta para usar esta función.',
      enableSync: 'Habilitar Sincronización',
      endSession: 'Terminar Sesión',
      logout: 'Cerrar Sesión',
    },
    common: {
      save: 'Guardar',
      cancel: 'Cancelar',
      edit: 'Editar',
      close: 'Cerrar',
      delete: 'Eliminar',
      confirm: 'Confirmar',
    },
  },
  'zh-TW': {
    title: '美國稅務計算器 2025',
    subtitle: '使用最新稅率和扣除額估算您的2025年聯邦和州所得稅。',
    disclaimer: '僅供規劃使用。非稅務或法律建議。請諮詢專業人士進行申報。',
    privacyNotice: '隱私聲明：所有計算均在您的瀏覽器本地運行。數據不會發送到伺服器。',
    disclaimerBanner: {
      title: '稅務估算工具 - 非專業建議',
      summary:
        '本計算器僅提供估算結果，實際稅務金額可能有所不同。如需準確申報，請諮詢合格的稅務專業人士或使用IRS核准的軟體。',
      accuracy: '準確性：',
      accuracyText: '我們力求基於2025年IRS規則提供準確計算，但稅法複雜且個人情況各異。',
      notAdvice: '非稅務建議：',
      notAdviceText: '本工具不構成稅務、法律或財務建議。我們不對基於這些估算所做的決定負責。',
      privacy: '隱私保護：',
      privacyText: '所有數據保存在您的設備上。我們不收集或傳輸個人稅務信息。',
      learnMore: '了解更多關於本工具',
      showLess: '收起',
      dismiss: '關閉提示',
    },
    onboarding: {
      welcome: {
        title: '歡迎使用美國稅務計算器 2025',
        description:
          '本工具幫助您估算聯邦和州所得稅。所有計算在您的設備上進行 - 數據不會離開您的電腦。',
      },
      privacy: {
        title: '您的數據保持私密',
        description:
          '我們非常重視您的隱私。所有稅務計算在瀏覽器本地運行。我們從不收集、存儲或傳輸您的個人稅務信息。',
      },
      features: {
        title: '主要功能',
        description: '支持多種收入類型、扣除額、稅收抵免和州稅計算。比較申報選項，發現節稅機會。',
      },
      help: {
        title: '獲取幫助',
        description:
          '表單欄位旁邊有幫助圖標(?)，可查看相關指導。稅務教育中心提供詳細的稅務概念說明。',
      },
      back: '上一步',
      next: '下一步',
      getStarted: '開始使用',
      skip: '跳過',
    },
    validation: {
      required: '此欄位為必填項',
      invalidSSN: '請輸入有效的社會安全號碼 (XXX-XX-XXXX)',
      negativeAmount: '金額不能為負數',
      tooLarge: '金額似乎過大',
      invalidDependents: '受撫養人數必須在0到20之間',
    },
    tabs: {
      personal: '個人資訊',
      income: '收入',
      payments: '付款',
      deductions: '扣除額',
    },
    personalInfo: {
      title: '個人資訊',
      firstName: '名字',
      lastName: '姓氏',
      ssn: '社會安全號碼',
      filingStatus: '申報狀態',
      address: '地址',
      dependents: '受撫養人數',
      marylandResident: '馬里蘭州居民',
      county: '馬里蘭縣/市',
      filingStatuses: {
        single: '單身',
        marriedJointly: '已婚聯合申報',
        marriedSeparately: '已婚分開申報',
        headOfHousehold: '戶主',
      },
      placeholders: {
        firstName: '輸入名字',
        lastName: '輸入姓氏',
        ssn: 'XXX-XX-XXXX',
        address: '輸入地址',
      },
      help: {
        ssn: '如社會安全卡上所示',
        dependents: '您支持的子女和其他符合條件的親屬',
        filingStatus: '截至2025年12月31日的狀態',
      },
    },
    spouseInfo: {
      title: '配偶資訊',
      subtitle: '如果已婚聯合申報，請填寫配偶資訊',
      firstName: '配偶名字',
      lastName: '配偶姓氏',
      ssn: '配偶社會安全號碼',
      wages: '配偶工資',
      withholding: '配偶預扣稅',
      recommendedFiling: '建議申報狀態：',
    },
    income: {
      title: '收入',
      wages: '工資和薪金 (W-2)',
      interestIncome: '利息收入 (1099-INT)',
      dividends: '股息收入 (1099-DIV)',
      capitalGains: '資本利得',
      businessIncome: '營業收入 (附表C)',
      otherIncome: '其他收入',
      k1Income: 'K-1 合夥企業/S公司收入',
      k1Section: {
        title: 'K-1表格 (合夥企業/S公司)',
        ordinaryIncome: '普通營業收入 (第1欄)',
        netRentalRealEstate: '淨租賃不動產收入 (第2欄)',
        otherRentalIncome: '其他租賃收入 (第3欄)',
        guaranteedPayments: '保證付款 (第4欄)',
        interestIncome: '利息收入 (第5欄)',
        dividends: '普通股息 (第6A欄)',
        royalties: '版稅 (第7欄)',
        netShortTermCapitalGain: '淨短期資本利得 (第8A欄)',
        netLongTermCapitalGain: '淨長期資本利得 (第9A欄)',
        otherPortfolioIncome: '其他投資組合收入 (第11欄)',
      },
      businessDetails: {
        title: '詳細營業收入 (附表C)',
        grossReceipts: '總收入或銷售額',
        costOfGoodsSold: '銷售成本',
        grossProfit: '毛利',
        businessExpenses: '營業費用總額',
        netBusinessIncome: '淨營業收入',
      },
    },
    payments: {
      title: '稅款支付和預扣',
      federalWithholding: '聯邦所得稅預扣 (W-2 第2欄)',
      stateWithholding: '馬里蘭州稅預扣 (W-2 第17欄)',
      estimatedTaxPayments: '2025年預估稅款支付',
      priorYearOverpayment: '上一年度超額付款已申請',
      otherPayments: '其他付款和抵免',
      paymentSummary: '付款摘要',
      totalPayments: '總付款額：',
      federalTaxOwed: '聯邦稅欠款：',
      stateTaxOwed: '州稅欠款：',
      localTaxOwed: '地方稅欠款：',
      totalTaxOwed: '總稅款欠額：',
    },
    deductions: {
      title: '扣除額',
      standardDeduction: '標準扣除額：',
      itemizeDeductions: '逐項扣除',
      mortgageInterest: '抵押貸款利息',
      stateLocalTaxes: '州和地方稅 (SALT) - 最高$10,000',
      charitableContributions: '慈善捐款',
      medicalExpenses: '醫療費用 (超過AGI的7.5%)',
      otherItemized: '其他逐項扣除',
    },
    credits: {
      title: '稅收抵免',
      childCare: {
        title: '兒童和受撫養人照顧抵免',
        hasExpenses: '我支付了兒童照顧或受撫養人照顧費用',
        numberOfQualifying: '符合條件的人數（13歲以下或殘疾）',
        expenses: '支付的照顧費用總額',
        maxExpenses: '最高：1人$3,000，2人或以上$6,000',
        help: '工作期間的托兒所、保姆、課前課後項目或日間夏令營費用',
      },
      education: {
        title: '教育抵免',
        hasExpenses: '我或我的受撫養人有合格的教育費用',
        studentName: '學生姓名',
        tuitionAndFees: '支付的學費和費用',
        yearsOfPostSecondary: '完成的高等教育年數',
        helpTuition: '來自學校提供的1098-T表格',
        helpYears: '美國機會抵免僅限於前4年',
        aotc: '美國機會抵免',
        llc: '終身學習抵免',
      },
    },
    results: {
      title: '稅務計算 (2025)',
      adjustedGrossIncome: '調整後總收入：',
      federalTaxableIncome: '聯邦應稅收入：',
      federalTax: '聯邦稅：',
      marylandTax: '馬里蘭州稅：',
      localTax: '地方稅：',
      totalTax: '總稅款：',
      totalPayments: '總付款額：',
      refundAmount: '退款金額：',
      amountOwed: '您應繳金額：',
      effectiveRate: '有效稅率：',
      marginalRate: '邊際稅率：',
      afterTaxIncome: '稅後收入：',
    },
    actions: {
      title: '操作',
      exportPDF: '匯出1040表格PDF',
      exportJSON: '匯出稅務資料 (JSON)',
      recalculate: '重新計算',
      startWizard: '啟動智能稅務嚮導',
      exitClassicMode: '退出經典模式',
      useClassicMode: '使用經典模式',
      importExport: '匯入/匯出',
      reviewCheck: '審核檢查',
      quickInterview: '快速問答',
      advanced: '進階功能',
      aiHelp: 'AI 幫助',
      proMode: '專業模式',
    },
    modernMode: {
      welcome: '歡迎使用智能稅務申報',
      welcomeDescription:
        '我們的智能嚮導將逐步引導您完成稅務申報，確保您不會錯過任何扣除額和抵免額，同時讓一切保持簡單。',
      smartGuidance: '智能指導',
      smartGuidanceDesc: '隨時提供上下文幫助和提示',
      accuracyCheck: '準確性檢查',
      accuracyCheckDesc: '內建驗證和優化功能',
      easyImport: '便捷匯入',
      easyImportDesc: '從去年或文檔匯入',
      startTaxReturn: '開始稅務申報',
      importData: '匯入資料',
      importDataDesc: '從去年或文檔匯入',
      reviewCheckTitle: '審核檢查',
      reviewCheckDesc: '準確性和優化',
      advancedTools: '進階工具',
      advancedToolsDesc: '複雜申報的強大功能',
      classicMode: '經典模式',
      classicModeDesc: '跳轉到詳細的標籤式輸入',
      currentSnapshot: '當前稅務概況',
      refundBalanceDue: '退款/應繳金額',
      refund: '退款：',
      owe: '應繳：',
      totalTax: '總稅款',
      effectiveRate: '有效稅率',
    },
    taxBrackets: {
      title: '2025年聯邦稅率級距',
      taxableIncome: '應稅收入',
      rate: '稅率',
    },
    marylandInfo: {
      title: '馬里蘭稅務資訊',
      stateRateRange: '州稅率範圍：',
      localTaxRate: '地方稅率',
      standardDeduction: 'MD標準扣除額：',
    },
    stateTaxSelector: {
      title: '州稅計算器',
      statesSupported: '個州已支援',
      selectState: '選擇您的居住州',
      noState: '無州稅',
      calculateState: '計算州稅',
    },
    languageSelector: {
      selectLanguage: '選擇語言',
      currentLanguage: '當前語言：',
    },
    advanced: {
      title: '進階功能',
      features: {
        taxPlanning: {
          title: '稅務規劃工具',
          description: '分析不同情境並優化您的稅務策略',
        },
        multiYear: {
          title: '多年度比較',
          description: '比較多年的稅務趨勢和變化',
        },
        auditSupport: {
          title: '稽核支援',
          description: '生成文件和報告以支援稽核',
        },
        portfolio: {
          title: '投資組合優化器',
          description: '優化投資以實現稅收效率',
        },
      },
    },
    dataManagement: {
      title: '資料管理',
      backup: {
        title: '備份和還原',
        createBackup: '建立備份',
        restoreBackup: '還原備份',
        importBackup: '匯入備份',
        exportBackup: '匯出備份',
        backupName: '備份名稱',
        backupDate: '備份日期',
        actions: '操作',
        restore: '還原',
        download: '下載',
        delete: '刪除',
        confirmDelete: '確定要刪除此備份嗎？',
        messages: {
          nameRequired: '請輸入備份名稱',
          success: '備份建立成功！',
          checksumMismatch: '備份資料可能已損壞。校驗和不符。',
          restoreSuccess: '資料還原成功！',
          restoreError: '還原備份時出錯：',
          importSuccess: '備份匯入成功！',
          importError: '匯入備份時出錯：',
        },
      },
    },
    audit: {
      title: '稽核支援和文件',
      documentation: '文件',
      report: '報告',
      checklist: '檢查清單',
      requiredDocuments: '所需文件',
      auditReport: '稽核報告',
      preparationChecklist: '準備檢查清單',
      auditTips: '稽核準備建議',
      tips: {
        tip1: '保持所有支援文件井然有序且易於存取',
        tip2: '至少保留記錄3年（某些情況下為7年）',
        tip3: '準備好解釋任何不尋常的扣除額或收入來源',
        tip4: '如果稽核複雜，請考慮專業代表',
        tip5: '及時且完整地回應IRS通訊',
      },
    },
    auth: {
      // 登入/會話
      secureAccess: '安全存取',
      passphraseDescription: '輸入密碼短語以加密您的稅務數據。這將創建一個安全的本地會話。',
      passphrase: '密碼短語',
      enterPassphrase: '輸入您的密碼短語',
      passphraseHint: '您的密碼短語在本地加密數據。我們不會看到或儲存它。',
      passphraseRequired: '請輸入密碼短語',
      localModeInfo: '您的數據已加密並僅儲存在此裝置上。無需帳戶。',
      continue: '繼續',
      loading: '請稍候...',

      // 雲端登入
      welcomeBack: '歡迎回來',
      loginDescription: '登入以跨裝置存取您儲存的稅務數據。',
      email: '電子郵件',
      enterEmail: '輸入您的郵件',
      emailRequired: '請輸入您的郵件',
      invalidEmail: '請輸入有效的郵件地址',
      password: '密碼',
      enterPassword: '輸入您的密碼',
      passwordRequired: '請輸入您的密碼',
      login: '登入',
      noAccount: '沒有帳戶？立即註冊',

      // 註冊
      createAccount: '建立帳戶',
      registerDescription: '註冊以啟用雲端同步，隨時隨地存取您的數據。',
      displayName: '顯示名稱',
      enterDisplayName: '您希望我們如何稱呼您？',
      optional: '選填',
      confirmPassword: '確認密碼',
      confirmPasswordPlaceholder: '再次輸入密碼',
      passwordTooShort: '密碼至少需要8個字元',
      passwordMismatch: '兩次輸入的密碼不一致',
      passwordRequirements: '至少8個字元',
      agreeToTerms: '我同意',
      termsOfService: '服務條款',
      and: '和',
      privacyPolicy: '隱私政策',
      mustAgreeToTerms: '您必須同意條款才能繼續',
      register: '建立帳戶',
      alreadyHaveAccount: '已有帳戶？立即登入',

      // 帳戶設定
      localUser: '本地用戶',
      localMode: '本地模式（數據儲存在裝置上）',
      cloudMode: '雲端模式（跨裝置同步）',
      profile: '個人資料',
      preferences: '偏好設定',
      security: '安全',
      notSet: '未設定',
      accountCreated: '帳戶建立於',
      lastLogin: '上次登入',

      // 偏好設定
      theme: '主題',
      themeSystem: '跟隨系統',
      themeLight: '淺色',
      themeDark: '深色',
      autoSave: '自動儲存',
      autoSaveDescription: '輸入時自動儲存更改',
      autoBackup: '自動備份',
      autoBackupDescription: '定期建立數據備份',
      showOnboarding: '顯示歡迎指南',
      showOnboardingDescription: '顯示新功能的歡迎指南',

      // 安全
      cloudSync: '雲端同步',
      cloudSyncDescription: '跨裝置同步您的加密數據',
      enableSyncTitle: '啟用雲端同步？',
      enableSyncDescription: '您的數據將在上傳前加密。您需要建立帳戶才能使用此功能。',
      enableSync: '啟用同步',
      endSession: '結束會話',
      logout: '登出',
    },
    common: {
      save: '儲存',
      cancel: '取消',
      edit: '編輯',
      close: '關閉',
      delete: '刪除',
      confirm: '確認',
    },
  },
};
